"use client"

import React from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatToRupee } from "@/lib/internalApis";
import { Wallet } from 'lucide-react';

interface WalletCreditInfoProps {
  walletBalance?: number;
  walletLimit?: number;
  availableCredit?: number;
}

const WalletCreditInfo: React.FC<WalletCreditInfoProps> = ({ 
  walletBalance = 0, 
  walletLimit = 0,
  availableCredit
}) => {
  // Calculate available credit if not provided
  const calculatedAvailableCredit = availableCredit || walletBalance + walletLimit;
  
  // Determine if we have a due balance (negative wallet balance)
  const hasDueBalance = walletBalance < 0;
  const dueBalance = hasDueBalance ? Math.abs(walletBalance) : 0;
  
  // Only show component if we have valid data
  const hasWalletData = walletBalance !== undefined || walletLimit !== undefined;
  
  if (!hasWalletData) return null;

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>
        <div className="text-[#084A76] text-[14px] py-1 px-2 hover:cursor-pointer">
          <span>Wallet Credit Info</span>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-[400px] mr-10">
        <div className="rounded-md w-full bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#084A76]">
                <TableHead className="!text-[10px] !font-semibold !text-white text-center">CATEGORY</TableHead>
                <TableHead className="!text-[10px] !font-semibold !text-white text-center">AMOUNT</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="!text-[12px] bg-muted/100 text-black font-light">
                <TableCell className="text-center">Credit</TableCell>
                <TableCell className="text-center">{formatToRupee(walletLimit)}</TableCell>
              </TableRow>
              {hasDueBalance ? (
                <TableRow className="!text-[12px] bg-muted/100 text-red-500 font-medium">
                  <TableCell className="text-center">Due Balance</TableCell>
                  <TableCell className="text-center">{formatToRupee(dueBalance)}</TableCell>
                </TableRow>
              ) : (
                <TableRow className="!text-[12px] bg-muted/100 text-black font-light">
                  <TableCell className="text-center">Wallet Balance</TableCell>
                  <TableCell className="text-center">{formatToRupee(walletBalance)}</TableCell>
                </TableRow>
              )}
              <TableRow className="!text-[12px] bg-muted/100 text-black font-light">
                <TableCell className="text-center">Available Credit</TableCell>
                <TableCell className="text-center">{formatToRupee(calculatedAvailableCredit)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export default WalletCreditInfo; 